/**
 * PLW glossary term slide-out — progressive enhancement for the
 * a[data-plw-glossary] anchors that snippets/plw-glossary-links.liquid injects
 * into PDP text. Intercepts plain left-clicks and opens the term's condensed
 * entry in the shared plw-overlay (id from data-overlay); modified clicks,
 * JS-off, and any failure navigate the anchor's real href instead — the
 * fallback IS the link.
 *
 *   <plw-glossary-overlay data-overlay data-section hidden>
 *     <div data-plw-glossary-entry="{handle}">…</div>  (own fit/material stash)
 *     <template data-plw-glossary-loading>…</template>
 *   </plw-glossary-overlay>
 *
 * Feature entries are fetched via the Section Rendering API
 * (`{href}?section_id={data-section}` → sections/plw-glossary-condensed.liquid)
 * and cached per handle; fit/material entries are pre-rendered in the stash
 * (they have no online-store URL to fetch). The overlay title is swapped to the
 * term's label per open. `aria-haspopup="dialog"` is added here on hydration —
 * with JS off the anchors are ordinary links, so Liquid must not claim a popup
 * — and re-applied on variant:update (the section morph re-renders PDP blocks,
 * dropping JS-added attributes).
 */

class PlwGlossaryOverlay extends HTMLElement {
  /** @type {Map<string, Element>} fetched feature fragments, keyed by handle */
  #cache = new Map();

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    document.addEventListener('click', this.#handleClick);
    document.addEventListener('variant:update', this.#hydrateLinks);
    this.#hydrateLinks();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleClick);
    document.removeEventListener('variant:update', this.#hydrateLinks);
    this.#abortController?.abort();
  }

  /** Mark the injected anchors as dialog openers (JS-on only). */
  #hydrateLinks = () => {
    for (const link of document.querySelectorAll('a[data-plw-glossary]')) {
      link.setAttribute('aria-haspopup', 'dialog');
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[data-plw-glossary]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const overlay = document.getElementById(this.dataset.overlay || '');
    const body = overlay?.querySelector('[data-plw-glossary-body]');
    // Missing shell or a non-overlay element under that id → let the link navigate.
    if (!overlay || !body || typeof (/** @type {any} */ (overlay).showDialog) !== 'function') return;

    const { plwGlossary: handle, plwGlossaryType: type, plwGlossaryLabel: label } = link.dataset;
    if (!handle) return;

    if (type === 'fit' || type === 'material') {
      const entry = this.querySelector(`[data-plw-glossary-entry="${CSS.escape(handle)}"]`);
      if (!entry) return;
      event.preventDefault();
      this.#open(overlay, body, label, entry.children);
      return;
    }

    event.preventDefault();
    const cached = this.#cache.get(handle);
    if (cached) {
      this.#open(overlay, body, label, cached.children);
      return;
    }

    // Show the sheet immediately with the loading state while the entry fetches.
    const loading = this.querySelector('template[data-plw-glossary-loading]');
    this.#open(overlay, body, label, loading instanceof HTMLTemplateElement ? [loading.content] : []);
    this.#fetchEntry(link, handle, overlay, body);
  };

  /**
   * @param {HTMLAnchorElement} link
   * @param {string} handle
   * @param {Element} overlay
   * @param {Element} body
   */
  async #fetchEntry(link, handle, overlay, body) {
    // #open (just called for the loading state) aborted any previous fetch.
    this.#abortController = new AbortController();

    try {
      // Build on the anchor's real href so locale/route prefixes are kept.
      const url = new URL(link.href);
      url.searchParams.set('section_id', this.dataset.section || '');

      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Glossary fragment failed (${response.status})`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fragment = doc.querySelector('[data-plw-glossary-condensed]');
      if (!fragment) throw new Error('Glossary fragment missing from section render');

      const stored = document.importNode(fragment, true);
      this.#cache.set(handle, stored);
      // The user may have opened another entry after the response landed.
      if (this.#abortController.signal.aborted) return;
      body.replaceChildren(...[...stored.children].map((child) => child.cloneNode(true)));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      // Degrade to the real page — the link was going there anyway.
      /** @type {any} */ (overlay).closeDialog();
      window.location.assign(link.href);
    }
  }

  /**
   * Fill the overlay and show it. Aborts any in-flight fetch first so a late
   * response can't clobber the entry the user opened afterwards.
   * @param {Element} overlay
   * @param {Element} body
   * @param {string | undefined} label
   * @param {Iterable<Node>} nodes - cloned into the body (sources stay intact)
   */
  #open(overlay, body, label, nodes) {
    this.#abortController?.abort();
    const title = document.getElementById(`${overlay.id}-title`);
    if (title && label) title.textContent = label;
    body.replaceChildren(...[...nodes].map((node) => node.cloneNode(true)));
    /** @type {any} */ (overlay).showDialog();
  }
}

if (!customElements.get('plw-glossary-overlay')) {
  customElements.define('plw-glossary-overlay', PlwGlossaryOverlay);
}
